import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Checkbox, FormControlLabel, TextField, Table, TableBody,
  TableCell, TableHead, TableRow, Chip
} from '@mui/material';
import axios from '../api/client';
import { useCurrency } from '../context/CurrencyContext';

// The optional late payment penalty on the payment capture dialogs.
//
// Discretionary by design: nothing here is applied unless someone ticks the box, and the
// amount can be reduced below what the terms give. So the panel's job is to show the
// working — which months are late, what each month's rent is, what the percentage gives —
// rather than just producing a number. Staff are deciding, and they can only decide from
// what they can see.
//
// The charge itself is computed and capped server-side. This is a view of that, not a
// second implementation of it.
const LatePenaltyPanel = ({ tenantId, value, onChange }) => {
  const { formatMoney } = useCurrency();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!tenantId) { setPreview(null); return; }
    setLoading(true);
    try {
      const { data } = await axios.get(`/payments/late-penalty/${tenantId}`);
      setPreview(data);
      // Seed the amount from the terms. Staff may reduce it; the server refuses more.
      onChange({ ...value, amount: data.suggested, percent: data.percent });
    } catch {
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <Typography variant="caption" color="text.secondary">Checking for overdue rent…</Typography>;
  }
  // Nothing overdue means nothing to penalise, and an unusable checkbox is worse than none.
  if (!preview || preview.monthsOverdue === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        {preview?.alreadyChargedCount > 0
          ? 'Every overdue month has already been penalised.'
          : 'No overdue rent — no late penalty applies.'}
      </Typography>
    );
  }

  const chargeable = preview.months.filter((m) => !m.alreadyCharged);
  const reduced = Number(value.amount) < preview.suggested - 0.01;

  return (
    <Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={!!value.enabled}
            onChange={(e) => onChange({ ...value, enabled: e.target.checked })}
          />
        }
        label={`Charge a late payment penalty — ${preview.monthsOverdue} overdue month(s)`}
      />

      {value.enabled && (
        <Box sx={{ mt: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Month</TableCell>
                <TableCell align="right">Rent</TableCell>
                <TableCell align="right">Late by</TableCell>
                <TableCell align="right">{preview.percent}%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {chargeable.map((m) => (
                <TableRow key={m.rentInvoiceId}>
                  <TableCell>{m.periodStart || m.rentInvoiceNumber}</TableCell>
                  {/* The rent billed for that month, not the balance left on it — the
                      penalty is on the month's rent, which is what was agreed. */}
                  <TableCell align="right">{formatMoney(m.rent, { decimals: 0 })}</TableCell>
                  <TableCell align="right">{m.daysLate} day(s)</TableCell>
                  <TableCell align="right">{formatMoney(m.penalty, { decimals: 0 })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <Box display="flex" gap={2} alignItems="flex-start" sx={{ mt: 2 }}>
            <TextField
              type="number"
              size="small"
              label="Amount to charge"
              value={value.amount ?? ''}
              onFocus={(e) => e.target.select()}
              onChange={(e) => onChange({ ...value, amount: e.target.value })}
              inputProps={{ min: 0, max: preview.suggested }}
              helperText={`Terms give ${formatMoney(preview.suggested, { decimals: 0 })}. You may charge less, not more.`}
              sx={{ minWidth: 220 }}
            />
            <TextField
              size="small"
              fullWidth
              required
              label="Reason"
              placeholder="e.g. Agreed terms, no notice given"
              value={value.reason || ''}
              onChange={(e) => onChange({ ...value, reason: e.target.value })}
              helperText="Recorded on the penalty invoice"
            />
          </Box>

          {reduced && (
            <Chip
              size="small"
              color="info"
              variant="outlined"
              sx={{ mt: 1 }}
              label={`Reduced by ${formatMoney(preview.suggested - Number(value.amount || 0), { decimals: 0 })}`}
            />
          )}

          <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1.5 }}>
            {/* Both facts surprise people, so they are stated where the decision is made. */}
            Raised as its own invoice and payable separately. Rent is always settled first,
            so this payment clears the rent before any of it reaches the penalty.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LatePenaltyPanel;
